import {
  CheckIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  ClockIcon,
  LinkIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { setLoanDetail } from 'actions'
import { PhoneLink } from 'components/PhoneLink'
import { useIsSharedLoan } from 'context/SharedLoanContext'
import { usePermissions } from 'hooks/usePermissions'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { LoanOverviewOverrides, saveLoanOverviewOverrides, updatePushLoanToNexysLockStatus } from 'services'
import { svgLoading } from 'stories/assets'
import { CopyableText, Tooltip } from 'stories/components'
import { confirm, formatDate, formatDateYMD, openAuditLog, phoneConvertor, replyTo } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

interface AdditionalInfoItem {
  title: string
  value: string
}

/**
 * A co-borrower on the loan. Co-borrowers double as the loan's guarantors here, which is why the
 * Borrower Information screen labels them "Co-Borrower/Guarantor" as well.
 */
export interface CoBorrowerContact {
  separator: string
  label: string
  name: string
  cellPhone: string
  homePhone: string
  workPhone: string
  email: string
}

/**
 * A holder of the loan, as the banner names it: the short code the lender is filed under plus the
 * lender's own name. `accountCode` is TMO's "Lender Account" where the tenant converted from it,
 * and empty for a position keyed in here.
 */
export interface LoanLenderSummary {
  accountCode: string
  name: string
  isLender?: boolean
}

interface InputProps {
  loanNumber: any
  program: string
  byteNumber: number
  entityTitle: string
  borrowerName: string
  propertyAddress: string
  mers?: string
  closingDate?: string
  /**
   * The loan's current maturity date, its next payment due date, and how many calendar days past
   * that due date it is. Passed only by the Loan Overview screen: this banner also renders as the
   * header of every other loan screen, and these cost two calls those screens have no use for.
   *
   * Left out, or empty, means the row is not drawn at all. That is what keeps an origination loan
   * with no servicing record from showing three blank servicing rows.
   */
  maturityDate?: string
  nextPaymentDate?: string
  daysPastDue?: number | null
  thirdPartyId?: string
  thirdPartyData?: {
    locked?: boolean
    source?: string
    hasNewData?: boolean
  }
  config?: Record<string, any>
  lenders?: LoanLenderSummary[]
  coBorrowers?: CoBorrowerContact[]
  additionalInfo?: AdditionalInfoItem[]
  onMergeNewData?: () => void
  action?: string
  onActionChange?: (action: string) => void
  overrides?: LoanOverviewOverrides
  onOverridesChange?: (overrides: LoanOverviewOverrides) => void
}

const MAX_VISIBLE_PROPERTY_ADDRESSES = 3

/**
 * Holders listed in the banner before it falls back to a count. A participated loan can carry six
 * or more, and the banner is a summary - the full list is Servicing > Investors.
 */
const MAX_VISIBLE_LENDERS = 3

export function LoanDetail({
  loanNumber,
  program,
  byteNumber,
  entityTitle,
  borrowerName,
  propertyAddress,
  mers,
  closingDate,
  maturityDate,
  nextPaymentDate,
  daysPastDue,
  thirdPartyId,
  thirdPartyData,
  config,
  lenders = [],
  coBorrowers = [],
  additionalInfo = [],
  onMergeNewData,
  action,
  onActionChange,
  overrides = {},
  onOverridesChange,
}: InputProps) {
  const dispatch = useDispatch()
  const [profile, loan, borrower] = useSelector((state: any) => [state.auth.profile, state.loan, state.borrower])
  const preferredLanguage = borrower?.borrower?.preferredLanguage || ''
  const accountType = profile.isBorrower ? 'borrower' : profile.accountType

  const { hasPermission } = usePermissions()
  const isShared = useIsSharedLoan()
  const seeLog = hasPermission('Global.AuditLog')
  const canEditLoanOverviewItem = hasPermission('Loan.Overview.Banner')

  const activeLoanNumber = Number(setLoanNumber() || loanNumber)

  const isNewLoan = loanNumber === 'New'

  // Editing state
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)
  const [viewAllAddresses, setViewAllAddresses] = useState(false)

  // Get editable state for each field, default to false if not configured
  const fieldsEditable = config?.overviewFieldsEditable || {}
  const isFieldEditable = (fieldKey: string) => {
    return fieldsEditable[fieldKey] === true
  }

  // Get displayed value (override value if exists, otherwise default)
  const getDisplayValue = (fieldKey: string, defaultValue: string) => {
    const overrideValue = overrides[fieldKey as keyof LoanOverviewOverrides]
    return overrideValue !== undefined && overrideValue !== '' ? overrideValue : defaultValue
  }

  // Start editing a field
  const startEditing = (fieldKey: string, currentValue: string) => {
    setEditingField(fieldKey)
    setEditValue(currentValue)
  }

  // Cancel editing
  const cancelEditing = () => {
    setEditingField(null)
    setEditValue('')
  }

  // Save edited value
  const saveEdit = async (fieldKey: string) => {
    if (!onOverridesChange) return

    setIsSaving(true)
    try {
      const newOverrides = { ...overrides, [fieldKey]: editValue }
      await saveLoanOverviewOverrides(newOverrides)
      onOverridesChange(newOverrides)
      toast('Override saved successfully', { type: 'success' })
      setEditingField(null)
      setEditValue('')
    } catch (error) {
      toast('Failed to save override', { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  // Clear override value
  const clearOverride = async (fieldKey: string) => {
    if (!onOverridesChange) return

    const result = await confirm('Are you sure you want to clear this override and show the default value?')
    if (!result) return

    setIsSaving(true)
    try {
      const newOverrides = { ...overrides }
      delete newOverrides[fieldKey as keyof LoanOverviewOverrides]
      await saveLoanOverviewOverrides(newOverrides)
      onOverridesChange(newOverrides)
      toast('Override cleared successfully', { type: 'success' })
    } catch (error) {
      toast('Failed to clear override', { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const showHistory = (table: string, field: string, keys: any, map?: any) => {
    if (isNewLoan) return
    const options = {
      table,
      field,
      keys,
      map,
    }
    openAuditLog(options)
  }

  const additionalPropertyAddresses = useMemo(() => {
    const primary = (propertyAddress || '').trim()
    return ((loan?.multipleProperties as any[]) || [])
      .map((item) => {
        const addr = String(item?.subjectPropertyAddress || '').trim()
        if (addr) return addr
        if (item?.subjectPropertyAddressTBD) return 'TBD'
        return ''
      })
      .filter((addr) => addr && addr !== primary)
  }, [loan?.multipleProperties, propertyAddress])

  const sizeClass = 'w-4 h-4'

  // Default order if not configured
  const defaultOrder = [
    'loanEmail',
    'loanNumber',
    'entityName',
    'borrowerName',
    'preferredLanguage',
    'propertyAddress',
    'loanProgram',
    'lenderAccount',
    'mers',
    'closingDate',
    'maturityDate',
    'nextPaymentDate',
    'daysLate',
    'thirdPartyId',
    'coBorrowerGuarantor',
  ]
  // DB/json sometimes nests a key as ["thirdPartyId"] instead of "thirdPartyId"; flatten so
  // includes() and field lookups stay string-keyed.
  const savedOrder = (config?.overviewFieldsOrder ?? [])
    .flat(Infinity)
    .filter((key: unknown): key is string => typeof key === 'string' && key.length > 0)
    .filter((key: string, index: number, arr: string[]) => arr.indexOf(key) === index)
  // A saved order was written before this field existed, so anything it does not mention is appended
  // rather than dropped. Without this a newly added banner field is invisible on every environment
  // whose admin has ever touched the field order, which is all of them.
  const fieldsOrder = (() => {
    const merged = savedOrder.length
      ? [...savedOrder, ...defaultOrder.filter((key) => key !== 'loanEmail' && !savedOrder.includes(key))]
      : defaultOrder
    // The address is meant to be grabbed without scrolling, so a banner order saved before it
    // existed puts it first rather than at the bottom. Once an admin drags it, that order sticks.
    return merged.includes('loanEmail') ? merged : ['loanEmail', ...merged]
  })()

  // A field the account-type config has never heard of defaults to visible, for the same reason the
  // order merge above exists: a saved config predating the field should not hide it.
  const isVisibleForAccountType = (fieldKey: string) =>
    !config || !!profile.type || config[fieldKey] === undefined || !!config[fieldKey]?.[accountType]

  // Get enabled state for each field, default to true if not configured
  const fieldsEnabled = config?.overviewFieldsEnabled || {}
  const isFieldEnabled = (fieldKey: string) => {
    // Loan Email is off until an admin turns it on. Every other banner field stays visible
    // unless it was explicitly hidden, so a field added later is not blanked out.
    if (fieldKey === 'loanEmail') return fieldsEnabled[fieldKey] === true
    return fieldsEnabled[fieldKey] !== false
  }

  // Render editable field with edit button or input
  const renderEditableField = (
    fieldKey: string,
    label: string,
    defaultValue: string,
    historyConfig?: { table: string; field: string; keys: any },
  ) => {
    const displayValue = getDisplayValue(fieldKey, defaultValue)
    const hasOverride =
      overrides[fieldKey as keyof LoanOverviewOverrides] !== undefined &&
      overrides[fieldKey as keyof LoanOverviewOverrides] !== ''
    const isEditing = editingField === fieldKey
    const canEdit = isFieldEditable(fieldKey) && !isNewLoan && canEditLoanOverviewItem

    if (isEditing) {
      return (
        <div className="flex mb-1 items-center">
          <div className="w-36">{label}:</div>
          <div className="flex items-center gap-2 ml-2 flex-1">
            <input
              type={fieldKey === 'closingDate' ? 'date' : 'text'}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-40 focus:outline-none focus:border-blue-500"
              autoFocus
            />
            <Tooltip message="Save">
              <span className="btn-icon text-green-600 hover:bg-green-100" onClick={() => saveEdit(fieldKey)}>
                {isSaving ? (
                  <img src={svgLoading} className="inline w-4 h-4 animate-spin" />
                ) : (
                  <CheckIcon className="w-4 h-4" />
                )}
              </span>
            </Tooltip>
            <Tooltip message="Cancel">
              <span className="btn-icon text-red-600 hover:bg-red-100" onClick={cancelEditing}>
                <XMarkIcon className="w-4 h-4" />
              </span>
            </Tooltip>
          </div>
        </div>
      )
    }

    return (
      <div className="flex mb-1">
        <div className="w-36">{label}:</div>
        <div className="flex items-center ml-2 group">
          <CopyableText>{displayValue}</CopyableText>
          {hasOverride && <span className="ml-1 text-xs text-blue-600 bg-blue-100 px-1 rounded">override</span>}
          {canEdit && (
            <Tooltip message="Edit override">
              <span
                className="text-gray-500 cursor-pointer hover-shadow1 rounded p-1 ml-1"
                onClick={() => startEditing(fieldKey, displayValue)}
              >
                <PencilIcon className={sizeClass} />
              </span>
            </Tooltip>
          )}
          {canEdit && hasOverride && (
            <Tooltip message="Clear override">
              <span
                className="text-red-500 cursor-pointer hover-shadow1 rounded p-1"
                onClick={() => clearOverride(fieldKey)}
              >
                <XMarkIcon className={sizeClass} />
              </span>
            </Tooltip>
          )}
          {seeLog && historyConfig && (
            <span className="text-gray-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover-shadow1 rounded p-1">
              <ClockIcon
                className={sizeClass}
                onClick={() => showHistory(historyConfig.table, historyConfig.field, historyConfig.keys)}
              />
            </span>
          )}
        </div>
      </div>
    )
  }

  // One label/value line inside the co-borrower block. Indented under its person so the banner reads
  // as "who" then "how to reach them" rather than as a flat list of unattributed phone numbers.
  const renderContactLine = (label: string, value: string, isPhone: boolean) => {
    if (!value) return null
    return (
      <div className="flex mb-1">
        <div className="w-36 pl-4 text-gray-500">{label}:</div>
        <div className="flex items-center ml-2">
          <CopyableText copyText={value}>
            {isPhone ? <PhoneLink value={value}>{phoneConvertor(value)}</PhoneLink> : value}
          </CopyableText>
        </div>
      </div>
    )
  }

  const fieldRenderers: Record<string, () => JSX.Element | null> = {
    loanEmail: () => {
      if (isShared) return null
      const id = activeLoanNumber
      if (!id || isNewLoan) return null
      const address = replyTo('loan', id)
      if (!address) return null
      return (
        <div className="flex mb-1">
          <div className="w-36">Loan Email:</div>
          <div className="flex items-center ml-2 min-w-0">
            <CopyableText copyText={address}>
              <span className="text-shade-blue italic break-all">{address}</span>
            </CopyableText>
          </div>
        </div>
      )
    },
    loanNumber: () => {
      if (!config || config.loanNumber?.[accountType] || profile.type) {
        const displayValue = getDisplayValue('loanNumber', String(byteNumber || loanNumber))
        const hasOverride = overrides.loanNumber !== undefined && overrides.loanNumber !== ''
        const isEditing = editingField === 'loanNumber'
        const canEdit = isFieldEditable('loanNumber') && !isNewLoan && canEditLoanOverviewItem

        if (isEditing) {
          return (
            <div className="flex mb-1 items-center">
              <div className="w-36">Loan Number:</div>
              <div className="flex items-center gap-2 ml-2 flex-1">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-40 focus:outline-none focus:border-blue-500"
                  autoFocus
                />
                <Tooltip message="Save">
                  <span className="btn-icon text-green-600 hover:bg-green-100" onClick={() => saveEdit('loanNumber')}>
                    {isSaving ? (
                      <img src={svgLoading} className="inline w-4 h-4 animate-spin" />
                    ) : (
                      <CheckIcon className="w-4 h-4" />
                    )}
                  </span>
                </Tooltip>
                <Tooltip message="Cancel">
                  <span className="btn-icon text-red-600 hover:bg-red-100" onClick={cancelEditing}>
                    <XMarkIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </div>
            </div>
          )
        }

        return (
          <div className="flex mb-1">
            <div className="w-36">Loan Number:</div>
            <div className="font-bold ml-2 flex items-center group">
              <CopyableText>{displayValue}</CopyableText>
              {hasOverride && (
                <span className="ml-1 text-xs text-blue-600 bg-blue-100 px-1 rounded font-normal">override</span>
              )}
              {canEdit && (
                <Tooltip message="Edit override">
                  <span
                    className="text-gray-500 cursor-pointer hover-shadow1 rounded p-1 ml-1"
                    onClick={() => startEditing('loanNumber', displayValue)}
                  >
                    <PencilIcon className={sizeClass} />
                  </span>
                </Tooltip>
              )}
              {canEdit && hasOverride && (
                <Tooltip message="Clear override">
                  <span
                    className="text-red-500 cursor-pointer hover-shadow1 rounded p-1"
                    onClick={() => clearOverride('loanNumber')}
                  >
                    <XMarkIcon className={sizeClass} />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        )
      }
      return null
    },
    entityName: () => {
      if (!config || config.entityName?.[accountType] || profile.type) {
        return renderEditableField('entityName', 'Entity Name', entityTitle, {
          table: 'Borrower',
          field: 'entityTitle',
          keys: { field: 'entityTitle', borrowerSeperator: 'borrower' },
        })
      }
      return null
    },
    borrowerName: () => {
      if (!config || config.borrowerName?.[accountType] || profile.type) {
        return renderEditableField('borrowerName', 'Borrower Name', borrowerName, {
          table: 'Borrower',
          field: 'firstName',
          keys: { field: 'firstName', borrowerSeperator: 'borrower' },
        })
      }
      return null
    },
    preferredLanguage: () => {
      // Always on the banner, blank included. A missing row is indistinguishable from a field the
      // account type hides, so a servicer about to call or write could not tell "nothing on file"
      // from "not shown here"; '-' says it outright. The value is set on Borrower Information.
      if (isVisibleForAccountType('preferredLanguage')) {
        return renderEditableField('preferredLanguage', 'Preferred Language', preferredLanguage || '-', {
          table: 'Borrower',
          field: 'preferredLanguage',
          keys: { field: 'preferredLanguage', borrowerSeperator: 'borrower' },
        })
      }
      return null
    },
    propertyAddress: () => {
      if (!config || config.propertyAddress?.[accountType] || profile.type) {
        const visibleExtras = viewAllAddresses
          ? additionalPropertyAddresses
          : additionalPropertyAddresses.slice(0, MAX_VISIBLE_PROPERTY_ADDRESSES - 1)
        const hasMore = additionalPropertyAddresses.length + 1 > MAX_VISIBLE_PROPERTY_ADDRESSES
        return (
          <div>
            {renderEditableField('propertyAddress', 'Property Address', propertyAddress, {
              table: 'Loan',
              field: 'subjectPropertyAddress',
              keys: { field: 'subjectPropertyAddress' },
            })}
            {visibleExtras.map((addr, index) => (
              <div className="flex mb-1" key={`${addr}-${index}`}>
                <div className="w-36" />
                <div className="flex items-center ml-2">
                  <CopyableText>{addr}</CopyableText>
                </div>
              </div>
            ))}
            {hasMore && (
              <div className="flex">
                <div className="w-36" />
                <span className="btn-link !text-sm ml-2" onClick={() => setViewAllAddresses((v) => !v)}>
                  {viewAllAddresses ? (
                    <span className="flex items-center py-1">
                      <ChevronDoubleUpIcon className="w-4 h-4 mr-1" />
                      View Less
                    </span>
                  ) : (
                    <span className="flex items-center py-1">
                      <ChevronDoubleDownIcon className="w-4 h-4 mr-1" />
                      View All
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        )
      }
      return null
    },
    loanProgram: () => {
      if (!config || config.loanProgram?.[accountType] || profile.type) {
        return renderEditableField('loanProgram', 'Loan Program', program)
      }
      return null
    },
    // Who holds the loan. Shown only where there is one, so an origination file - and a tenant that
    // tracks no participations at all - gets no empty row. Read-only here: the holders come from
    // the loan's participations, and Servicing > Investors is where they are changed.
    lenderAccount: () => {
      if (!lenders.length || !isVisibleForAccountType('lenderAccount')) return null
      const visibleLenders = lenders.slice(0, MAX_VISIBLE_LENDERS)
      const hiddenLenderCount = lenders.length - visibleLenders.length
      return (
        <div className="flex mb-1">
          <div className="w-36">Lender Account:</div>
          <div className="ml-2 flex flex-col gap-y-0.5">
            {visibleLenders.map((lender, index) => (
              <div key={`lender-${index}`} className="flex items-center flex-wrap gap-x-2">
                {/* The codes are a column of their own, so several lenders read as a list rather
                    than as names scattered across the row. Wide enough for TMO's ten characters
                    plus the copy icon CopyableText always reserves. */}
                <div className="min-w-[9rem]">
                  <CopyableText>{lender.accountCode || lender.name}</CopyableText>
                </div>
                {!!lender.accountCode && !!lender.name && <span className="text-gray-500">{lender.name}</span>}
              </div>
            ))}
            {hiddenLenderCount > 0 && (
              <span className="text-gray-500">
                +{hiddenLenderCount} more in Servicing &gt; Investors
              </span>
            )}
          </div>
        </div>
      )
    },
    mers: () => {
      if (!config || config.mers?.[accountType] || profile.type) {
        return renderEditableField('mers', 'MERS', mers || '')
      }
      return null
    },
    closingDate: () => {
      if (!config || config.closingDate?.[accountType] || profile.type) {
        const displayValue = getDisplayValue('closingDate', closingDate || '')
        const hasOverride = overrides.closingDate !== undefined && overrides.closingDate !== ''
        const isEditing = editingField === 'closingDate'
        const canEdit = isFieldEditable('closingDate') && !isNewLoan && canEditLoanOverviewItem

        if (isEditing) {
          return (
            <div className="flex mb-1 items-center">
              <div className="w-36">Closing Date:</div>
              <div className="flex items-center gap-2 ml-2 flex-1">
                <input
                  type="date"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-40 focus:outline-none focus:border-blue-500"
                  autoFocus
                />
                <Tooltip message="Save">
                  <span className="btn-icon text-green-600 hover:bg-green-100" onClick={() => saveEdit('closingDate')}>
                    {isSaving ? (
                      <img src={svgLoading} className="inline w-4 h-4 animate-spin" />
                    ) : (
                      <CheckIcon className="w-4 h-4" />
                    )}
                  </span>
                </Tooltip>
                <Tooltip message="Cancel">
                  <span className="btn-icon text-red-600 hover:bg-red-100" onClick={cancelEditing}>
                    <XMarkIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </div>
            </div>
          )
        }

        return (
          <div className="flex mb-1">
            <div className="w-36">Closing Date:</div>
            <div className="flex items-center ml-2 group">
              <CopyableText>{displayValue ? formatDate(displayValue) : ''}</CopyableText>
              {hasOverride && <span className="ml-1 text-xs text-blue-600 bg-blue-100 px-1 rounded">override</span>}
              {canEdit && (
                <Tooltip message="Edit override">
                  <span
                    className="text-gray-500 cursor-pointer hover-shadow1 rounded p-1 ml-1"
                    onClick={() => startEditing('closingDate', displayValue)}
                  >
                    <PencilIcon className={sizeClass} />
                  </span>
                </Tooltip>
              )}
              {canEdit && hasOverride && (
                <Tooltip message="Clear override">
                  <span
                    className="text-red-500 cursor-pointer hover-shadow1 rounded p-1"
                    onClick={() => clearOverride('closingDate')}
                  >
                    <XMarkIcon className={sizeClass} />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        )
      }
      return null
    },
    // The three servicing rows below are deliberately read-only, unlike the fields above them. Each
    // is the figure a servicing screen already shows, read from the same service that screen and the
    // pipeline column read, so the banner cannot quote a maturity or a due date that disagrees with
    // Servicing Information. An override here would be a second source for a number whose whole job
    // is to be the one number.
    maturityDate: () => {
      if (!maturityDate || !isVisibleForAccountType('maturityDate')) return null
      // A maturity date that has gone by is the thing somebody scanning this banner is looking for,
      // and it reads as ordinary text otherwise. Elapsed, not in default: grace and the balance
      // decide whether the loan badges as a default, but a date in the past is worth seeing. Same
      // treatment as the past-due date below, and the same as the Loan Details panel gives both.
      const isMatured = formatDateYMD(maturityDate) < formatDateYMD()
      return (
        <div className="flex mb-1">
          <div className="w-36">Maturity Date:</div>
          <div className="flex items-center ml-2">
            <CopyableText copyText={formatDate(maturityDate)}>
              <span className={isMatured ? 'font-semibold text-red-600' : ''}>{formatDate(maturityDate)}</span>
            </CopyableText>
          </div>
        </div>
      )
    },
    nextPaymentDate: () => {
      if (!nextPaymentDate || !isVisibleForAccountType('nextPaymentDate')) return null
      const isDelinquent = formatDateYMD(nextPaymentDate) < formatDateYMD()
      return (
        <div className="flex mb-1">
          <div className="w-36">Next Payment Due:</div>
          <div className="flex items-center ml-2">
            <CopyableText copyText={formatDate(nextPaymentDate)}>
              <span className={isDelinquent ? 'font-semibold text-red-600' : ''}>{formatDate(nextPaymentDate)}</span>
            </CopyableText>
          </div>
        </div>
      )
    },
    daysLate: () => {
      // `0` is a real answer and must still draw: it is how a current loan is told apart from one
      // with no due date on file at all, which is what `null`/absent means and why that one is
      // dropped instead.
      if (daysPastDue === null || daysPastDue === undefined) return null
      if (!isVisibleForAccountType('daysLate')) return null
      // The due date comes back from the same read as the count, so it is always there when the
      // count is. Phrased without it anyway rather than risk printing an unparsed date.
      const dueOn = nextPaymentDate ? formatDate(nextPaymentDate) : ''
      const tooltip =
        daysPastDue > 0
          ? `Calendar days since the payment${
              dueOn ? ` due ${dueOn}` : ''
            }, with nothing subtracted for grace. Past due is not the same as in default: grace decides that.`
          : `Not past due.${dueOn ? ` The next payment is due ${dueOn}.` : ''}`
      return (
        <div className="flex mb-1">
          <div className="w-36">Days Late:</div>
          <div className="flex items-center ml-2">
            <Tooltip message={tooltip}>
              <span className={daysPastDue > 0 ? 'font-semibold text-red-600' : ''}>{daysPastDue}</span>
            </Tooltip>
          </div>
        </div>
      )
    },
    thirdPartyId: () => {
      // if (!thirdPartyId && !overrides.thirdPartyId) return null
      if (!config || config.thirdPartyId?.[accountType] || profile.type) {
        const locked = thirdPartyData?.locked
        const displayValue = getDisplayValue('thirdPartyId', thirdPartyId || '')
        const hasOverride = overrides.thirdPartyId !== undefined && overrides.thirdPartyId !== ''
        const isEditing = editingField === 'thirdPartyId'
        const canEdit = isFieldEditable('thirdPartyId') && !isNewLoan && canEditLoanOverviewItem

        const onReleaseLock = async () => {
          const result = await confirm(`Are you sure you want to ${locked ? 'Release' : 'Lock'} the loan?`)
          if (!result) return
          if (onActionChange) onActionChange('releaseLock')
          const newThirdPartyData = { ...thirdPartyData, locked: !locked }
          await updatePushLoanToNexysLockStatus({
            locked: !locked,
          })
          toast(`Loan ${locked ? 'Released' : 'Locked'} successfully.`, { type: 'success' })
          dispatch(setLoanDetail({ thirdPartyData: newThirdPartyData }))
          if (onActionChange) onActionChange('')
        }

        const renderReleaseLock = () => {
          if (!thirdPartyData) return null
          if (thirdPartyData?.source !== 'nexys') return null
          if (action === 'releaseLock')
            return <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />

          const Icon = locked ? LockClosedIcon : LockOpenIcon
          return (
            <Tooltip message={locked ? 'Release Loan' : 'Lock Loan'}>
              <span className="btn-icon" onClick={() => onReleaseLock()}>
                <Icon className="w-4 h-4" />
              </span>
            </Tooltip>
          )
        }

        const renderMergeNewData = () => {
          if (!thirdPartyData || !thirdPartyData?.hasNewData) return null
          return (
            <Tooltip message={'Merge New Data'}>
              <span className="btn-icon" onClick={() => onMergeNewData && onMergeNewData()}>
                <LinkIcon className="w-4 h-4" />
              </span>
            </Tooltip>
          )
        }

        if (isEditing) {
          return (
            <div className="flex mb-1 items-center">
              <div className="w-36">ThirdParty ID:</div>
              <div className="flex items-center gap-2 ml-2 flex-1">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-40 focus:outline-none focus:border-blue-500"
                  autoFocus
                />
                <Tooltip message="Save">
                  <span className="btn-icon text-green-600 hover:bg-green-100" onClick={() => saveEdit('thirdPartyId')}>
                    {isSaving ? (
                      <img src={svgLoading} className="inline w-4 h-4 animate-spin" />
                    ) : (
                      <CheckIcon className="w-4 h-4" />
                    )}
                  </span>
                </Tooltip>
                <Tooltip message="Cancel">
                  <span className="btn-icon text-red-600 hover:bg-red-100" onClick={cancelEditing}>
                    <XMarkIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </div>
            </div>
          )
        }

        return (
          <div className="flex mb-1">
            <div className="w-36">ThirdParty ID:</div>
            <div className="flex items-center gap-2 ml-2 group">
              <CopyableText>{displayValue}</CopyableText>
              {hasOverride && <span className="ml-1 text-xs text-blue-600 bg-blue-100 px-1 rounded">override</span>}
              {canEdit && (
                <Tooltip message="Edit override">
                  <span
                    className="text-gray-500 cursor-pointer hover-shadow1 rounded p-1 ml-1"
                    onClick={() => startEditing('thirdPartyId', displayValue)}
                  >
                    <PencilIcon className={sizeClass} />
                  </span>
                </Tooltip>
              )}
              {canEdit && hasOverride && (
                <Tooltip message="Clear override">
                  <span
                    className="text-red-500 cursor-pointer hover-shadow1 rounded p-1"
                    onClick={() => clearOverride('thirdPartyId')}
                  >
                    <XMarkIcon className={sizeClass} />
                  </span>
                </Tooltip>
              )}
              {thirdPartyData && (
                <>
                  {renderReleaseLock()}
                  {renderMergeNewData()}
                </>
              )}
            </div>
          </div>
        )
      }
      return null
    },
    coBorrowerGuarantor: () => {
      // Nothing to draw when the loan has no co-borrowers; an empty labelled row would be noise on
      // the majority of loans. The records themselves are maintained on Borrower Information.
      if (!coBorrowers.length) return null
      // Opt-in, unlike the older banner fields: borrower contact details are not something every
      // tenant wants on a screen this widely visible, so an admin turns it on per account type
      // under Global Settings > Loan Overview Banner.
      if (!config?.coBorrowerGuarantor?.[accountType]) return null
      // A record can exist with nothing filled in yet; skip it rather than print a bare label.
      const withData = coBorrowers.filter(
        (item) => item.name || item.cellPhone || item.homePhone || item.workPhone || item.email,
      )
      if (!withData.length) return null
      return (
        <div>
          {withData.map((item) => (
            <div key={item.separator} className="mb-1">
              <div className="flex mb-1">
                <div className="w-36">{item.label}:</div>
                <div className="flex items-center ml-2">
                  <CopyableText>{item.name || '-'}</CopyableText>
                </div>
              </div>
              {renderContactLine('Cell Phone', item.cellPhone, true)}
              {renderContactLine('Home Phone', item.homePhone, true)}
              {renderContactLine('Work Phone', item.workPhone, true)}
              {renderContactLine('Email', item.email, false)}
            </div>
          ))}
        </div>
      )
    },
  }

  return (
    <div className="text-[14px] text-left">
      {fieldsOrder.map((fieldKey: string) => {
        // Check if field is enabled before rendering
        if (!isFieldEnabled(fieldKey)) {
          return null
        }
        const renderer = fieldRenderers[fieldKey]
        return renderer ? <div key={fieldKey}>{renderer()}</div> : null
      })}
      {additionalInfo.map((item, index) => (
        <div key={`additional-${index}`} className="flex mb-1">
          <div className="w-36">{item.title}:</div>
          <div className="flex items-center ml-2">
            <CopyableText>{item.value}</CopyableText>
          </div>
        </div>
      ))}
    </div>
  )
}
